<template>
    <div
        v-if="
            type === 'multi-select' &&
            showSelectAllOption &&
            shouldShow &&
            !disabled
        "
        class="d-flex justify-content-start align-items-start flex-wrap"
        style="gap: 1rem"
    >
        <!-- Multi-select with "Select All" option -->
        <Field
            v-if="!isAllSites"
            :type="type"
            :label="label"
            :disabled="disabled"
            :key="siteOptions.length"
            v-model="selectedSite"
            :required="required"
            :plain-text-mode="plainTextMode"
            :params="{ options: siteOptions, placeholder: placeholder }"
            class="flex-grow-1"
        />
        <Field
            v-else
            :label="label"
            :disabled="true"
            :required="required"
            v-model="allSitesInputText"
            class="flex-grow-1"
        />
        <Field
            class="pt-4"
            label="Select All Sites"
            type="checkbox"
            :modelValue="isAllSites"
            @update:modelValue="toggleAllSites"
            :disabled="disabled"
        />
    </div>
    <div
        v-else-if="shouldShow"
        class="d-flex align-items-end"
        style="gap: 0.5rem"
    >
        <Field
            :type="type"
            :label="label"
            :key="siteOptions.length"
            :disabled="disabled"
            :disabled-tooltip="disabledTooltip"
            v-model="selectedSite"
            :required="required"
            :plain-text-mode="plainTextMode"
            :params="{ options: siteOptions, placeholder: placeholder }"
            class="flex-grow-1"
        />
    </div>
    <slot v-if="shouldShow"></slot>
</template>

<script>
import Field from '@/components/Field.vue';

export default {
    name: 'SiteDropdownField',
    components: {
        Field,
    },
    props: {
        modelValue: {
            type: [String, Array],
            default: null,
        },
        label: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: 'select',
        },
        required: {
            type: Boolean,
            default: false,
        },
        showSelectAllOption: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'Select a site',
        },
        plainTextMode: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hideWhenGlobalActive: {
            type: Boolean,
            default: false,
        },
        hideWhenSingleSiteUser: {
            type: Boolean,
            default: true,
        },
        disabledTooltip: {
            type: String,
            default: '',
        },
        // Custom options override (e.g., for template-filtered sites)
        options: {
            type: Array,
            default: null,
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            isAllSites: false,
            preSelectedSites: [],
            internalUpdate: false,
        };
    },
    computed: {
        siteOptions() {
            // Use custom options if provided, otherwise use store
            return this.options || this.$siteStore.getSiteOptions || [];
        },
        allSitesInputText() {
            return `All ${this.siteOptions.length} sites`;
        },
        selectedSite: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            },
        },
        // Check if global site is active
        isGlobalSiteActive() {
            const globalId = this.$siteStore.globalSiteId;
            return globalId !== null && globalId !== undefined;
        },
        shouldShow() {
            if (this.hideWhenGlobalActive && this.isGlobalSiteActive) {
                return false;
            }
            if (
                this.hideWhenSingleSiteUser &&
                this.$siteStore.isSingleSiteUserView
            ) {
                return false;
            }
            return true;
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(newVal, oldVal) {
                if (!this.showSelectAllOption) return;
                if (this.internalUpdate) return; // Skip if update came from toggleAllSites

                const wasAllSites = this.isAllSites;
                const isNowAllSites = this.hasAllOptions(
                    newVal,
                    this.siteOptions
                );

                // Transition to all-sites
                if (!wasAllSites && isNowAllSites) {
                    // Save prior selection only when coming from a partial selection
                    this.preSelectedSites = Array.isArray(oldVal)
                        ? [...oldVal]
                        : [];
                    this.isAllSites = true;
                    return;
                }

                // Transition away from all-sites
                if (wasAllSites && !isNowAllSites) {
                    this.isAllSites = false;
                }
            },
        },
        siteOptions: {
            immediate: true,
            handler(opts) {
                if (!this.showSelectAllOption) return;
                this.isAllSites = this.hasAllOptions(this.modelValue, opts);
            },
        },
    },
    methods: {
        toggleAllSites(newValue) {
            this.internalUpdate = true;

            if (newValue) {
                // Save current selection before selecting all
                this.preSelectedSites = this.modelValue || [];
                this.isAllSites = true;
                // Select all site values
                const allSiteValues = this.siteOptions.map(
                    (site) => site.value
                );
                this.$emit('update:modelValue', allSiteValues);
            } else {
                // Restore previous selection
                this.isAllSites = false;
                this.$emit('update:modelValue', this.preSelectedSites);
            }

            this.$nextTick(() => {
                this.internalUpdate = false;
            });
        },

        // Checks if the selected options are all the options
        hasAllOptions(val, opts) {
            if (!Array.isArray(val) || !opts || opts.length === 0) return false;
            const selected = new Set(val);
            return opts.every((o) => selected.has(o.value));
        },
    },
};
</script>
